import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Calculator, TrendingUp, Download, Share2, BookOpen, Info } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const ARECreationSimulator = () => {
  const [inputs, setInputs] = useState({
    salaireBrutMensuel: '3000',
    dureeContrat: '24',
    revenuActivite: '0',
    age: 'moins55'
  });
  const [results, setResults] = useState({});

  const calculate = () => {
    const salaireBrut = parseFloat(inputs.salaireBrutMensuel) || 0;
    const duree = parseFloat(inputs.dureeContrat) || 24;
    const revenuActivite = parseFloat(inputs.revenuActivite) || 0;

    const sjr = (salaireBrut * 12) / 365;

    // Calcul ARE : max entre 40.4% SJR + 13.18€ et 57% SJR
    const formule1 = sjr * 0.404 + 13.18;
    const formule2 = sjr * 0.57;
    let areJournaliere = Math.max(formule1, formule2);

    // Plafond à 75% du SJR
    areJournaliere = Math.min(areJournaliere, sjr * 0.75);
    // Minimum 32.13€
    areJournaliere = Math.max(areJournaliere, 32.13);

    const areMensuelle = areJournaliere * 30;

    // Durée d'indemnisation selon l'âge
    let dureeMoisMax;
    if (inputs.age === 'plus57') dureeMoisMax = 27;
    else if (inputs.age === '55-56') dureeMoisMax = 22.5;
    else dureeMoisMax = 18;

    const dureeEffective = Math.min(duree, dureeMoisMax);
    const droitsTotaux = areMensuelle * dureeEffective;

    // Cumul ARE + revenus d'activité (depuis avril 2025 : max 60% des droits)
    const areCumulee = revenuActivite > 0 ? Math.max(0, areMensuelle - (revenuActivite * 0.70)) : areMensuelle;
    const revenuTotalMensuel = areCumulee + revenuActivite;

    setResults({
      sjr: sjr.toFixed(2),
      areJournaliere: areJournaliere.toFixed(2),
      areMensuelle: Math.round(areMensuelle),
      dureeEffective,
      droitsTotaux: Math.round(droitsTotaux),
      areCumulee: Math.round(areCumulee),
      revenuTotalMensuel: Math.round(revenuTotalMensuel),
      dureeMoisMax
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'ARE + Création - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - ARE + Création Entreprise', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`Salaire brut mensuel : ${parseInt(inputs.salaireBrutMensuel).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`ARE journalière : ${results.areJournaliere} EUR`, 15, y); y += 7;
    doc.text(`ARE mensuelle : ${results.areMensuelle?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Durée max : ${results.dureeMoisMax} mois`, 15, y); y += 7;
    doc.text(`Droits totaux : ${results.droitsTotaux?.toLocaleString()} EUR`, 15, y); y += 7;
    if (parseFloat(inputs.revenuActivite) > 0) {
      doc.text(`ARE cumulée avec activité : ${results.areCumulee?.toLocaleString()} EUR/mois`, 15, y); y += 7;
      doc.text(`Revenu total mensuel : ${results.revenuTotalMensuel?.toLocaleString()} EUR`, 15, y); y += 7;
    }
    y += 10;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : France Travail 2026, Code du travail, Unédic', 15, y);
    doc.save('simulation-are-creation-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> ARE + Création
            </CardTitle>
            <CardDescription>Estimez vos droits ARE en créant votre entreprise</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Dernier salaire brut mensuel (€)</Label>
              <Input data-testid="salaire-input" type="text" inputMode="numeric" value={inputs.salaireBrutMensuel}
                onChange={(e) => handleInputChange('salaireBrutMensuel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="3000" className="mt-1" />
            </div>
            <div>
              <Label>Durée du dernier contrat (mois)</Label>
              <Input data-testid="duree-input" type="text" inputMode="numeric" value={inputs.dureeContrat}
                onChange={(e) => handleInputChange('dureeContrat', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="24" className="mt-1" />
            </div>
            <div>
              <Label>Âge à la fin du contrat</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[['moins55', '< 55 ans'], ['55-56', '55-56 ans'], ['plus57', '57+ ans']].map(([key, label]) => (
                  <Button key={key} size="sm" data-testid={`age-${key}-btn`}
                    variant={inputs.age === key ? 'default' : 'outline'}
                    className={inputs.age === key ? 'bg-blue-600 hover:bg-blue-700' : ''}
                    onClick={() => handleInputChange('age', key)}>{label}</Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Revenus mensuels de votre activité (€) - optionnel</Label>
              <Input data-testid="revenu-activite-input" type="text" inputMode="numeric" value={inputs.revenuActivite}
                onChange={(e) => handleInputChange('revenuActivite', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">Pour simuler le cumul ARE + revenus de votre nouvelle activité</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>France Travail - Règlement d'assurance chômage 2025-2026</li>
                    <li>ARE : max(40.4% SJR + 13.18€, 57% SJR), plafond 75% SJR</li>
                    <li>Minimum ARE : 32.13€/jour</li>
                    <li>Durée max : 18 mois ({'<'}55), 22.5 mois (55-56), 27 mois (57+)</li>
                    <li>Cumul ARE + création : max 60% droits (réforme avril 2025)</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" /> Vos droits ARE
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">SJR</p>
                <p className="text-lg font-bold text-blue-900" data-testid="result-sjr">{results.sjr}€</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">ARE journalière</p>
                <p className="text-lg font-bold text-blue-900">{results.areJournaliere}€</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-sm text-gray-600 mb-1">ARE mensuelle</p>
              <p className="text-3xl font-black text-green-900" data-testid="result-are-mensuelle">{results.areMensuelle?.toLocaleString()}€</p>
              <p className="text-xs text-gray-500">pendant {results.dureeEffective} mois max</p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-sm text-purple-600 mb-1">Droits totaux estimés</p>
              <p className="text-2xl font-bold text-purple-900" data-testid="result-droits-totaux">{results.droitsTotaux?.toLocaleString()}€</p>
            </div>

            {parseFloat(inputs.revenuActivite) > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-semibold text-amber-800 mb-2">Cumul ARE + revenus d'activité</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <p className="text-xs text-amber-600">ARE maintenue</p>
                    <p className="text-lg font-bold text-amber-900">{results.areCumulee?.toLocaleString()}€</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-green-600">Revenu total</p>
                    <p className="text-lg font-bold text-green-900">{results.revenuTotalMensuel?.toLocaleString()}€</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : France Travail (ex-Pôle emploi) 2026 - Unédic</p>
              <p>francetravail.org | unedic.org | service-public.fr</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ARECreationSimulator;
